import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Dumbbell,
  Hand,
  HeartPulse,
  Sparkles,
  Timer,
  ArrowRight,
  MessageCircle,
  Gift,
  Zap,
  Droplets,
  Bone,
} from "lucide-react";
import { MESSENGER_URL, BOOKING_URL } from "@/constants/content";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };

const STRETCHES = [
  {
    title: "Combhajlító nyújtás",
    how:
      "Állj csípőszéles terpeszbe, tedd az egyik sarkad előre, lábujjak felfelé. Csípőből dőlj előre egyenes háttal, amíg a comb hátsó részén finom húzást érzel. Tartsd 30 másodpercig, majd oldalt válts.",
  },
  {
    title: "Csípőhajlító kitörés",
    how:
      "Lépj nagy kitörésbe, a hátsó térd a talajhoz közel. Told a csípőd finoman előre, amíg a hátsó láb elülső csípőjén nyúlást érzel. Ez az ülőmunka és a futás után is aranyat ér. 30 mp oldalanként.",
  },
  {
    title: "Vádli nyújtás falnál",
    how:
      "Támaszkodj a falnak, egyik lábad hátra, sarok a földön, térd nyújtva. Told a csípőd a fal felé, amíg a vádliban húzást érzel. Fontos a sok terhelt láb regenerációjához. 30 mp oldalanként.",
  },
  {
    title: "Mellkas- és vállnyitás",
    how:
      "Állj egy ajtókeretbe, alkarod a keretre, könyök vállmagasságban. Lépj finoman előre, amíg a mellkas és a váll elején nyúlást érzel. Kiegyenlíti az edzés és a görnyedés okozta zártságot.",
  },
];

const SELF_MASSAGE = [
  {
    title: "Talpgördítés teniszlabdával",
    how:
      "Ülve vagy állva görgesd a talpad egy teniszlabdán, a saroktól a lábujjakig. A talpi fascia lazítása felszabadítja az egész hátsó izomláncot – meglepően sokat segít a comb- és derékfeszülésen is. 1-2 perc lábanként.",
  },
  {
    title: "Comb hengerelése (SMR)",
    how:
      "SMR-hengeren (vagy összetekert törölközőn) görgesd végig a comb elülső és külső oldalát lassan. Ahol érzékeny pontot találsz, ott időzz 20-30 másodpercet, nyugodt légzéssel. Oldja a letapadt kötőszövetet.",
  },
  {
    title: "Vádli önmasszázs",
    how:
      "Ülve tedd az egyik bokádat a másik térdére, és a hüvelykujjaddal masszírozd a vádlit alulról felfelé, körkörös mozdulatokkal. Segít a tejsav ürítésében és a nehéz láb érzés oldásában. 1-2 perc lábanként.",
  },
  {
    title: "Tarkó nyomáspont-oldás",
    how:
      "Kulcsold össze az ujjaid a tarkódon, és a két hüvelykujjaddal keresd meg a koponyaalapnál lévő mélyedéseket. Körkörös, mély nyomással masszírozd 1 percig – oldja a fejfájást és a nyakfeszülést.",
  },
];

const MASSAGE_POINTS = [
  {
    title: "Gyorsított tejsav-kiürülés",
    text:
      "Speciális fogások segítenek a felhalmozódott salakanyagokat és tejsavat gyorsabban visszaterelni a keringésbe, így a napokig tartó izomláz drasztikusan lerövidül.",
  },
  {
    title: "Az izompólyák (fascia) lazítása",
    text:
      "A futástól az izmokat körülvevő hártya letapadhat, ami beszűkült mozgáshoz és sérülésekhez vezet. A masszázs ezt fellazítja, javítva a lépéshosszodat és a rugalmasságodat.",
  },
  {
    title: "Gyorsabb sejtszintű beépülés",
    text:
      "A masszázs fokozza a helyi vérkeringést. Ha közvetlenül utána viszed be a prémium aminosavakat és ízületvédőket (mint az AMJ), azok sokkal gyorsabban és hatékonyabban jutnak el a megdolgoztatott izmokhoz és porcokhoz.",
  },
];

const PRODUCTS = [
  {
    icon: Zap,
    timing: "Edzés előtt",
    name: "Az Éjszakai Turbó – Energy Go Stix",
    problem:
      "Egész napos hajtás után nehéz fejben és fizikailag is megérkezni a futópályára.",
    what:
      "Természetes koffeinforrásokkal (guarana, zöld tea) garantálja az éles fókuszt – hirtelen pulzusugrás vagy későbbi „összeomlás” nélkül.",
    plusLabel: "A sportoló plusz",
    plus:
      "A benne lévő L-Arginin tágítja az ereket, fokozza az izmok oxigénellátását, így sokkal később savasodsz be.",
    accent: "#A88A2B",
  },
  {
    icon: Droplets,
    timing: "Edzés közben",
    name: "A Gyomorbarát Sportzselé – RioVida Burst",
    problem:
      "A sűrű, mesterséges sportzseléktől sok futónak begörcsöl a gyomra mozgás közben.",
    what:
      "Antioxidáns-bomba, ami tiszta gyümölcspépből áll (açaí, áfonya, gránátalma). Kíméletes a hasadhoz, és egyenletes gyümölcscukor-energiát biztosít.",
    plusLabel: "Az esti előny",
    plus:
      "Teljesen koffeinmentes – megadja a kakaót a távhoz, de az éjszakai alvásodat nem fogja elrontani.",
    accent: "#2A9D8F",
  },
  {
    icon: Bone,
    timing: "Ízületvédelem",
    name: "Az ízületek életbiztosítása – Mozaikdarabkák a porcoknak (AMJ)",
    problem:
      "A futás (különösen aszfalton) hatalmas rázkódással és mikrosérülésekkel jár a térdnek, a bokának és a csípőnek.",
    what:
      "A glükózamin és kondroitin mellett olyan természetes gyulladáscsökkentő növényi kivonatokat tartalmaz, mint a kurkuma és a tömjénfa (Boswellia).",
    plusLabel: "A sportoló plusz",
    plus:
      "Kenést ad az ízületeknek, és azonnal oltja a tüzet (a mikrogyulladásokat) az izmaidban, hogy a másnap reggeli felkelés ne legyen kínszenvedés.",
    accent: "#1C6B61",
  },
];

export default function RegeneraciosKisokos() {
  return (
    <div data-testid="regeneracios-kisokos-page" className="text-[#1A2825]">
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-10">
        <motion.div initial="hidden" animate="show" variants={fadeUp}>
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#A88A2B]">
            <Dumbbell className="h-3.5 w-3.5" /> Sportolóknak · Ajándék
          </span>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light text-[#1A2825] mt-4 leading-[1.05]">
            Regenerációs <em className="not-italic text-[#2A9D8F]">Kisokos</em>
          </h1>
          <p className="mt-6 text-lg text-[#3A4D4A] font-light leading-relaxed">
            A kemény edzés csak a fele a fejlődésnek – a másik fele a regeneráció. Ez a kisokos
            négy erőteljes eszközt ad a kezedbe: <strong>nyújtás, önmasszázs, belső táplálás
            (4Life) és masszázs</strong>. Használd edzés után, pihenőnapon vagy egy hosszú nap
            végén.
          </p>
          <p className="mt-4 text-[#A88A2B] italic font-light">
            A harmónia belül kezdődik és kívül látszik.
          </p>
        </motion.div>
      </section>

      {/* 1. Nyújtó fogások */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-14">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-11 w-11 rounded-2xl bg-[#2A9D8F]/10 border border-[#2A9D8F]/25 flex items-center justify-center">
            <Timer className="h-5 w-5 text-[#2A9D8F]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#A88A2B]">1. rész</div>
            <h2 className="font-serif-display text-2xl md:text-3xl leading-snug">Nyújtó fogások</h2>
          </div>
        </div>
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 md:grid-cols-2 gap-5"
        >
          {STRETCHES.map((s, i) => (
            <motion.div
              key={s.title}
              variants={fadeUp}
              data-testid={`stretch-card-${i}`}
              className="bg-white rounded-[1.5rem] border border-[#EAE5D9] p-6 shadow-[0_8px_28px_rgba(26,40,37,0.04)]"
            >
              <div className="flex items-baseline gap-3">
                <span className="font-serif-display text-2xl text-[#2A9D8F]">{i + 1}</span>
                <h3 className="font-serif-display text-xl leading-snug">{s.title}</h3>
              </div>
              <p className="mt-3 text-sm text-[#3A4D4A] font-light leading-relaxed">{s.how}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* 2. Önmasszázs fogások */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-14">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-11 w-11 rounded-2xl bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center">
            <Hand className="h-5 w-5 text-[#A88A2B]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#A88A2B]">2. rész</div>
            <h2 className="font-serif-display text-2xl md:text-3xl leading-snug">Önmasszázs fogások</h2>
          </div>
        </div>
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 md:grid-cols-2 gap-5"
        >
          {SELF_MASSAGE.map((s, i) => (
            <motion.div
              key={s.title}
              variants={fadeUp}
              data-testid={`selfmassage-card-${i}`}
              className="bg-white rounded-[1.5rem] border border-[#EAE5D9] p-6 shadow-[0_8px_28px_rgba(26,40,37,0.04)]"
            >
              <div className="flex items-baseline gap-3">
                <span className="font-serif-display text-2xl text-[#A88A2B]">{i + 1}</span>
                <h3 className="font-serif-display text-xl leading-snug">{s.title}</h3>
              </div>
              <p className="mt-3 text-sm text-[#3A4D4A] font-light leading-relaxed">{s.how}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* 3. Belső Tuning – termékek */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-14">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-2xl bg-[#1C6B61]/10 border border-[#1C6B61]/25 flex items-center justify-center">
            <Zap className="h-5 w-5 text-[#1C6B61]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#A88A2B]">3. rész</div>
            <h2 className="font-serif-display text-2xl md:text-3xl leading-snug">
              A belső tuning – a tiszta hatóanyagok ereje
            </h2>
          </div>
        </div>
        <p className="text-sm md:text-base text-[#3A4D4A] font-light leading-relaxed mb-8 max-w-3xl">
          Hobbi sportolóként a legnagyobb kihívás, hogy a munka, a család és a mindennapi pörgés
          mellett kell odatenned magad az edzéseken. Nem mindegy, milyen üzemanyagot adsz a
          testednek. Felejtsd el a cukros energiaitalokat és a műanyag ízű zseléket – íme a tiszta,
          sejtszintű támogatás:
        </p>
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-5"
        >
          {PRODUCTS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.name}
                variants={fadeUp}
                data-testid={`product-card-${i}`}
                className="bg-[#F6F3EB] rounded-[1.5rem] border border-[#EAE5D9] p-6 flex flex-col"
              >
                <div className="flex items-center gap-2">
                  <span
                    className="h-9 w-9 rounded-xl flex items-center justify-center border"
                    style={{ color: p.accent, borderColor: `${p.accent}40`, background: `${p.accent}12` }}
                  >
                    <Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: p.accent }}>
                    {p.timing}
                  </span>
                </div>
                <h3 className="font-serif-display text-lg leading-snug text-[#1A2825] mt-4">{p.name}</h3>
                <p className="mt-3 text-sm text-[#3A4D4A] font-light leading-relaxed italic">{p.problem}</p>
                <p className="mt-3 text-sm text-[#3A4D4A] font-light leading-relaxed">
                  <span className="font-medium text-[#1A2825]">Mit tud? </span>
                  {p.what}
                </p>
                <p className="mt-3 text-sm text-[#3A4D4A] font-light leading-relaxed">
                  <span className="font-medium" style={{ color: p.accent }}>{p.plusLabel}: </span>
                  {p.plus}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
        <div className="mt-6">
          <Link
            to="/4life"
            data-testid="kisokos-4life-link"
            className="inline-flex items-center gap-2 text-[#1C6B61] hover:text-[#2A9D8F] text-sm font-medium transition-colors"
          >
            A teljes 4Life kínálat megtekintése <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* 4. Külső támogatás – masszőr tippek */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-14">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-2xl bg-[#2A9D8F]/10 border border-[#2A9D8F]/25 flex items-center justify-center">
            <HeartPulse className="h-5 w-5 text-[#2A9D8F]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#A88A2B]">4. rész</div>
            <h2 className="font-serif-display text-2xl md:text-3xl leading-snug">
              A külső támogatás – a masszőr tippjei
            </h2>
          </div>
        </div>
        <p className="text-sm md:text-base text-[#3A4D4A] font-light leading-relaxed mb-8 max-w-3xl">
          A belső táplálás félsiker, ha a megfáradt izmok mechanikusan nincsenek karbantartva.
          A sport- és svédmasszázs nem luxus, hanem a sérülésmentes sportolás alapja.
        </p>
        <div className="bg-white rounded-[1.75rem] border border-[#EAE5D9] p-7 md:p-9 shadow-[0_8px_28px_rgba(26,40,37,0.04)]">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#A88A2B] mb-5">
            Mi történik a testeddel a masszázságyon?
          </div>
          <ul className="space-y-5">
            {MASSAGE_POINTS.map((m, i) => (
              <li key={i} className="flex gap-4">
                <span className="font-serif-display text-2xl text-[#2A9D8F] leading-none shrink-0">
                  {i + 1}
                </span>
                <div>
                  <div className="font-medium text-[#1A2825]">{m.title}</div>
                  <p className="mt-1 text-sm md:text-base text-[#3A4D4A] font-light leading-relaxed">
                    {m.text}
                  </p>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-7 rounded-2xl bg-[#1C6B61]/8 border border-[#1C6B61]/20 p-5 flex gap-3">
            <Sparkles className="h-5 w-5 text-[#A88A2B] shrink-0 mt-0.5" />
            <p className="text-sm md:text-base text-[#1A2825] font-light leading-relaxed">
              <span className="font-medium">A profi formula:</span> Kívülről a szakértő kezek,
              belülről a prémium 4Life hatóanyagok. Ez a titka annak, hogy hobbi sportolóként is
              fejlődni tudj – sérülések nélkül.
            </p>
          </div>
        </div>
      </section>


      {/* Bónusz kupon */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          data-testid="kisokos-coupon"
          className="relative overflow-hidden rounded-[2rem] bg-[#1A2825] text-white p-8 md:p-12"
        >
          <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#2A9D8F]/20 blur-3xl" />
          <div className="absolute -bottom-20 -left-10 h-52 w-52 rounded-full bg-[#D4AF37]/15 blur-3xl" />
          <div className="relative">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">
              <Gift className="h-4 w-4" /> Bónusz ajándék
            </span>
            <h2 className="font-serif-display text-3xl md:text-4xl font-light mt-4 leading-snug">
              10% kedvezmény a következő masszázsodból
            </h2>
            <p className="mt-4 text-white/75 font-light leading-relaxed max-w-xl">
              Mert a valódi regeneráció szakértő kezekben teljesedik ki. Foglalj időpontot, és a
              lenti kóddal 10%-ot spórolsz az első kezelésen.
            </p>
            <div className="mt-6 inline-flex items-center gap-3 rounded-2xl border border-dashed border-[#D4AF37]/50 bg-white/5 px-6 py-4">
              <span className="text-xs uppercase tracking-[0.25em] text-white/60">Kuponkód</span>
              <span
                data-testid="kisokos-coupon-code"
                className="font-serif-display text-2xl md:text-3xl text-[#D4AF37] tracking-widest"
              >
                4lifemozgas
              </span>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="kisokos-book-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#2A9D8F] hover:bg-[#22857a] text-white px-7 py-3.5 rounded-full font-medium tracking-wide transition-all shadow-[0_10px_24px_rgba(42,157,143,0.35)] hover:-translate-y-0.5"
              >
                <MessageCircle className="h-4 w-4" /> Időpontot foglalok
              </a>
              <Link
                to="/masszazs"
                data-testid="kisokos-massage-link"
                className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white/50 text-white px-7 py-3.5 rounded-full font-medium tracking-wide transition-all"
              >
                Masszázs kezelések <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <p className="mt-5 text-[11px] text-white/45">
              2026. augusztus 31-ig érvényes. A kupon az első kezelésre vonatkozik, más akcióval nem vonható össze.
            </p>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
